// ═══════════════════════════════════════════════════════════════════════════════
// UPPER ECHELON AUTOMOTIVE — PRIVATE ADMIN APPOINTMENTS CALENDAR
//
// Visit https://ueauto.store/#admin-calendar to open this. It is NOT linked
// anywhere in the site nav — reachable only if you go to that URL directly
// (bookmark it). The hash itself is not the security boundary: no customer
// data is ever sent to this page until the correct PIN is verified by your
// backend, so the URL being "guessable" doesn't expose anything.
//
// How it works:
//   1. This screen asks for your admin PIN.
//   2. The PIN is sent to your Render backend (UEA_BACKEND below) in a
//      request header — never checked here in the browser.
//   3. Your backend checks the PIN against process.env.ADMIN_PIN and, only
//      if it matches, looks up orders via the Shopify Admin API and returns
//      appointment details (name, phone, email, address, vehicle, etc).
//   4. If the PIN is wrong, the backend returns 401 and nothing is shown.
//
// This file pairs with the new backend route: routes/admin-appointments.js
// (see that file + the setup instructions for the pieces this depends on).
// ═══════════════════════════════════════════════════════════════════════════════

(function () {
  'use strict';

  // Your existing Render backend (same one used for /send-sms etc.)
  const UEA_BACKEND = 'https://uea-backend-3.onrender.com';

  const esc = s => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  let pin = null; // kept in memory only — never persisted to localStorage/sessionStorage
  let appointments = [];
  let loading = false;
  let error = '';
  let view = { year: new Date().getFullYear(), month: new Date().getMonth() };
  let selectedDate = null;

  function isOpen() {
    return location.hash === '#admin-calendar';
  }

  function mount() {
    const root = document.getElementById('uea-admin-app');
    if (!root) return;
    if (!isOpen()) {
      root.style.display = 'none';
      root.innerHTML = '';
      document.documentElement.style.overflow = '';
      return;
    }
    root.style.display = 'block';
    document.documentElement.style.overflow = 'hidden';
    render();
  }

  function render() {
    const root = document.getElementById('uea-admin-app');
    if (!root) return;

    if (!pin) {
      root.innerHTML = pinScreen();
      bindPinScreen();
      return;
    }

    root.innerHTML = appScreen();
    bindAppScreen();
  }

  function pinScreen() {
    return `
      <div class="ua-wrap">
        <div class="ua-pin-box">
          <div class="ua-title" style="font-size:1.1rem;">Admin Access</div>
          <p style="color:var(--cream-dim); font-size:0.85rem; margin-top:0.5rem;">Enter your admin PIN to view appointments.</p>
          <input type="password" inputmode="numeric" id="ua-pin-input" placeholder="••••" autofocus>
          <button class="ua-btn" id="ua-pin-submit" style="width:100%;">Unlock Calendar</button>
          <div class="ua-error">${esc(error)}</div>
        </div>
      </div>`;
  }

  function bindPinScreen() {
    const input = document.getElementById('ua-pin-input');
    const submit = document.getElementById('ua-pin-submit');
    const go = () => {
      const val = (input.value || '').trim();
      if (!val) return;
      pin = val;
      error = '';
      loading = true;
      render();
      fetchAppointments();
    };
    submit?.addEventListener('click', go);
    input?.addEventListener('keydown', e => { if (e.key === 'Enter') go(); });
  }

  async function fetchAppointments() {
    try {
      const res = await fetch(`${UEA_BACKEND}/admin/appointments`, {
        headers: { 'x-admin-pin': pin }
      });
      if (res.status === 401) {
        pin = null;
        error = 'Incorrect PIN.';
        loading = false;
        render();
        return;
      }
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      appointments = data.appointments || [];
      loading = false;
      render();
    } catch (err) {
      console.error('Admin calendar: failed to load appointments —', err);
      error = 'Could not reach the server. Try again shortly.';
      pin = null;
      loading = false;
      render();
    }
  }

  function apptsByDate() {
    const map = {};
    appointments.forEach(a => {
      const key = a.date || 'unknown';
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }

  function appScreen() {
    if (loading) {
      return `<div class="ua-wrap"><p style="text-align:center;color:var(--cream-dim);margin-top:6rem;">Loading appointments…</p></div>`;
    }

    const byDate = apptsByDate();
    const first = new Date(view.year, view.month, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const todayIso = new Date().toISOString().split('T')[0];

    // The site stores "Preferred Date" as a formatted string like "Tue, Sep 30, 2026"
    // (see fmtDate() in the booking widget). Build a lookup keyed the same way
    // so calendar cells (which iterate real dates) can match against it.
    const fmtLikeBooking = d => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

    let cells = '';
    for (let i = 0; i < startWeekday; i++) cells += `<div class="ua-cal-cell empty"></div>`;
    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(view.year, view.month, d);
      const key = fmtLikeBooking(cellDate);
      const dayAppts = byDate[key] || [];
      const iso = cellDate.toISOString().split('T')[0];
      const isToday = iso === todayIso;
      cells += `
        <div class="ua-cal-cell ${isToday ? 'today' : ''}" data-day-key="${esc(key)}">
          <div class="ua-daynum">${d}</div>
          ${dayAppts.slice(0, 3).map(a => `<div class="ua-appt-pill ${a.rush ? 'rush' : ''}">${esc(a.time || '')} ${esc(a.customerName || '').split(' ')[0] || ''}</div>`).join('')}
          ${dayAppts.length > 3 ? `<div class="ua-appt-pill">+${dayAppts.length - 3} more</div>` : ''}
        </div>`;
    }

    const dayList = selectedDate ? (byDate[selectedDate] || []) : [];

    return `
      <div class="ua-wrap">
        <div class="ua-header">
          <div class="ua-title">📅 Appointments</div>
          <div style="display:flex;gap:0.6rem;">
            <button class="ua-btn" id="ua-refresh">Refresh</button>
            <button class="ua-btn" id="ua-close">Close</button>
          </div>
        </div>

        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem;">
          <button class="ua-btn" id="ua-prev-month">‹ Prev</button>
          <div class="ua-title" style="font-size:1.05rem;">${monthLabel}</div>
          <button class="ua-btn" id="ua-next-month">Next ›</button>
        </div>

        <div class="ua-cal-grid">
          ${['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => `<div class="ua-cal-dow">${d}</div>`).join('')}
        </div>
        <div class="ua-cal-grid">${cells}</div>

        <div style="margin-top:2rem;">
          <div class="ua-title" style="font-size:1rem;margin-bottom:1rem;">
            ${selectedDate ? esc(selectedDate) : 'Click a day above to see full details'}
          </div>
          ${selectedDate && dayList.length === 0 ? `<div class="ua-empty">No appointments that day.</div>` : ''}
          ${dayList.map(a => `
            <div class="ua-list-card ${a.rush ? 'rush' : ''}">
              <div class="ua-list-row"><span>Time</span><span>${esc(a.time || '—')}${a.rush ? ' ⚡ RUSH' : ''}</span></div>
              <div class="ua-list-row"><span>Customer</span><span>${esc(a.customerName || '—')}</span></div>
              <div class="ua-list-row"><span>Phone</span><span>${esc(a.phone || '—')}</span></div>
              ${a.email ? `<div class="ua-list-row"><span>Email</span><span>${esc(a.email)}</span></div>` : ''}
              <div class="ua-list-row"><span>Location</span><span>${esc(a.location || '—')}</span></div>
              <div class="ua-list-row"><span>Vehicle</span><span>${esc(a.vehicle || '—')}</span></div>
              <div class="ua-list-row"><span>Service</span><span>${esc(a.service || '—')}</span></div>
              ${a.problemDescription ? `<div class="ua-list-row"><span>Problem</span><span>${esc(a.problemDescription)}</span></div>` : ''}
              ${a.notes ? `<div class="ua-list-row"><span>Notes</span><span>${esc(a.notes)}</span></div>` : ''}
              <div class="ua-list-row"><span>Order</span><span>${esc(a.orderName || '—')}</span></div>
            </div>`).join('')}
        </div>
      </div>`;
  }

  function bindAppScreen() {
    document.getElementById('ua-refresh')?.addEventListener('click', () => { loading = true; render(); fetchAppointments(); });
    document.getElementById('ua-close')?.addEventListener('click', () => { location.hash = ''; });
    document.getElementById('ua-prev-month')?.addEventListener('click', () => {
      view.month -= 1; if (view.month < 0) { view.month = 11; view.year -= 1; }
      render();
    });
    document.getElementById('ua-next-month')?.addEventListener('click', () => {
      view.month += 1; if (view.month > 11) { view.month = 0; view.year += 1; }
      render();
    });
    document.querySelectorAll('[data-day-key]').forEach(el => {
      el.addEventListener('click', () => { selectedDate = el.dataset.dayKey; render(); });
    });
  }

  window.addEventListener('hashchange', mount);
  document.addEventListener('DOMContentLoaded', mount);
  if (document.readyState !== 'loading') mount();
})();